import React from 'react';
import { InfoIcon } from '@cp-shared-19/frontend-ui';
import { TotalEarlySettlementDetails, EarlySettlementSubsidyDetails, PartialEarlySettlementDetails } from 'cpes-common';
import { DescriptionListAccordion } from '@vwfs-bronson/bronson-react';
import { TranslationFormat, useTranslationWithFormatting } from 'localization/useTranslationWithFormatting';
import { CPDate } from '@cp-shared-19/common-utilities';

type DetailField = {
    key: string;
    label: React.ReactNode;
    value: string | number;
};

type IconLabelProps = {
    text: string;
    tooltip: string;
};
const IconLabel: React.FC<IconLabelProps> = ({ text, tooltip }) => {
    return (
        <>
            {text}
            <InfoIcon text={tooltip} />
        </>
    );
};

const getField = (
    val: string | number | undefined,
    label: string,
    visible: boolean,
    tooltip?: string,
): DetailField | undefined => {
    return visible && typeof val !== 'undefined'
        ? {
              key: label,
              label: tooltip ? <IconLabel text={label} tooltip={tooltip} /> : label,
              value: val,
          }
        : undefined;
};

const getVisibleFields = (fields: (DetailField | undefined)[]): DetailField[] => {
    return fields
        .filter((field): field is DetailField => !!field)
        .map((field, index) => ({ ...field, key: `${field.key}-${index}` }));
};

type CalculationDetailsRowProps = {
    title: React.ReactNode;
    value: React.ReactNode;
    details?: DetailField[];
    tooltip?: string;
};

const CalculationDetailsRow: React.FC<CalculationDetailsRowProps> = ({ title, value, details = [], tooltip }) => {
    const rowTitle = tooltip ? <IconLabel text={String(title)} tooltip={tooltip} /> : title;

    return (
        <DescriptionListAccordion.Item title={rowTitle} titleSplit={value} defaultOpen>
            {details.map(field => (
                <DescriptionListAccordion.DL key={field.key} label={field.label}>
                    {field.value}
                </DescriptionListAccordion.DL>
            ))}
        </DescriptionListAccordion.Item>
    );
};

const getValueOrZero = (val: number | undefined): number => {
    return val ? val : 0;
};

const getPartialEarlySettlementAmount = (
    amortizedCapital: number | undefined,
    accruedInterestAmount: number | undefined,
    settlementFeeAmount: number | undefined,
    pendingSettlements: number | undefined,
    openingComAmount: number | undefined,
): number => {
    return (
        getValueOrZero(amortizedCapital) +
        getValueOrZero(accruedInterestAmount) +
        getValueOrZero(settlementFeeAmount) -
        getValueOrZero(pendingSettlements) +
        getValueOrZero(openingComAmount)
    );
};

type CalculationDetailsProps = {
    settlementDetails: TotalEarlySettlementDetails | PartialEarlySettlementDetails;
    subsidyDetails: EarlySettlementSubsidyDetails;
    partialSettlementPaymentDate?: CPDate;
    partialSettlementTotalAmount?: number;
};

export const CalculationDetails: React.FC<CalculationDetailsProps> = ({
    settlementDetails,
    subsidyDetails,
    partialSettlementPaymentDate,
    partialSettlementTotalAmount,
}) => {
    const { t, f } = useTranslationWithFormatting('early-settlement');

    const isPartialEarlySettlement = !!partialSettlementPaymentDate;

    const {
        installmentsToSettle,
        accruedInterestAmount,
        settlementFeeAmount,
        settlementFeeRate,
        pendingSettlements,
        unpaidBalance,
        nextDueDate,
        openingComAmount,
    } = settlementDetails;

    const totalEarlySettlementDetails = settlementDetails as TotalEarlySettlementDetails;
    const partialEarlySettlementDetails = settlementDetails as PartialEarlySettlementDetails;
    const outstandingCapital = !isPartialEarlySettlement ? totalEarlySettlementDetails.outstandingCapital : undefined;
    const amortizedCapital = isPartialEarlySettlement ? partialEarlySettlementDetails.amortizedCapital : undefined;
    const pendingArrears = isPartialEarlySettlement ? partialEarlySettlementDetails.pendingArrears : undefined;
    const pendingCollateral = !isPartialEarlySettlement ? totalEarlySettlementDetails.pendingCollateral : undefined;
    const settlementAmount = isPartialEarlySettlement
        ? getPartialEarlySettlementAmount(
              amortizedCapital,
              accruedInterestAmount,
              settlementFeeAmount,
              pendingSettlements,
              openingComAmount,
          )
        : totalEarlySettlementDetails.settlementAmount;
    const paymentDate = isPartialEarlySettlement
        ? partialSettlementPaymentDate
        : totalEarlySettlementDetails.paymentDate;
    const totalAmount = isPartialEarlySettlement
        ? partialSettlementTotalAmount
        : totalEarlySettlementDetails.totalAmount;

    const unpaidBalanceTitle = isPartialEarlySettlement
        ? `${t('calculation-details.unpaid-balance.field')} + ${t(
              'calculation-details.settlement-details.field.pending-arrears',
          )}`
        : t('calculation-details.unpaid-balance.field');

    const unpaidBalanceAmount = isPartialEarlySettlement
        ? getValueOrZero(unpaidBalance) + getValueOrZero(pendingArrears)
        : unpaidBalance;

    const {
        freeInstallment,
        autoInsurance,
        breakdownInsurance,
        maintenance,
        discountPlanE,
        primaryDiscountVWFS,
        secondaryDiscountVWFS,
        subsidyAmount,
    } = subsidyDetails;

    const subsidyFields = getVisibleFields([
        getField(
            f(freeInstallment, TranslationFormat.CURRENCY),
            t('penalty-amount-details.free-installment'),
            !!freeInstallment,
        ),
        getField(
            f(autoInsurance, TranslationFormat.CURRENCY),
            t('penalty-amount-details.auto-insurance'),
            !!autoInsurance,
        ),
        getField(
            f(breakdownInsurance, TranslationFormat.CURRENCY),
            t('penalty-amount-details.breakdown-insurance'),
            !!breakdownInsurance,
        ),
        getField(f(maintenance, TranslationFormat.CURRENCY), t('penalty-amount-details.maintenance'), !!maintenance),
        getField(
            f(discountPlanE, TranslationFormat.CURRENCY),
            t('penalty-amount-details.discount-plan-e'),
            !!discountPlanE,
        ),
        getField(
            f(primaryDiscountVWFS, TranslationFormat.CURRENCY),
            t('penalty-amount-details.discount-vwfs'),
            !!primaryDiscountVWFS,
        ),
        getField(
            f(secondaryDiscountVWFS, TranslationFormat.CURRENCY),
            t('penalty-amount-details.discount-vwfs'),
            !!secondaryDiscountVWFS,
        ),
    ]);

    const settlementFields = getVisibleFields([
        getField(
            installmentsToSettle,
            t('calculation-details.settlement-details.field.settled-period'),
            !!installmentsToSettle,
            t('calculation-details.settlement-details.tooltip.settled-period'),
        ),
        getField(
            f(outstandingCapital, TranslationFormat.CURRENCY),
            t('calculation-details.settlement-details.field.pending-capital-amount'),
            !isPartialEarlySettlement,
        ),
        getField(
            f(amortizedCapital, TranslationFormat.CURRENCY),
            t('calculation-details.settlement-details.field.amortization-capital'),
            isPartialEarlySettlement,
        ),
        getField(
            f(accruedInterestAmount, TranslationFormat.CURRENCY),
            t('calculation-details.settlement-details.field.accrued-interest-amount'),
            !!accruedInterestAmount,
            t('calculation-details.settlement-details.tooltip.accrued-interest-amount'),
        ),
        getField(
            f(settlementFeeAmount, TranslationFormat.CURRENCY),
            t('calculation-details.settlement-details.field.settlement-fee-amount'),
            !!settlementFeeAmount,
            t('calculation-details.settlement-details.tooltip.settlement-fee-amount'),
        ),
        getField(
            f(settlementFeeRate, TranslationFormat.PERCENT_NOT_ROUNDED),
            t('calculation-details.settlement-details.field.settlement-fee-rate'),
            !!settlementFeeRate,
            t('calculation-details.settlement-details.tooltip.settlement-fee-rate'),
        ),
        getField(
            f(pendingSettlements, TranslationFormat.CURRENCY),
            t('calculation-details.settlement-details.field.upfront-paid-installment'),
            !!pendingSettlements,
        ),
        getField(
            f(pendingCollateral, TranslationFormat.CURRENCY),
            t('calculation-details.settlement-details.field.pending-collateral'),
            !!pendingCollateral,
        ),
        getField(
            f(openingComAmount, TranslationFormat.CURRENCY),
            t('calculation-details.settlement-details.field.opening-com-amount'),
            !!openingComAmount,
        ),
    ]);

    return (
        <div className="u-text-left calculation-details-accordion">
            <DescriptionListAccordion
                headerSplit
                multipleOpen
                totalLabel={t('calculation-details.total-amount')}
                totalLabelNote={
                    <>
                        <div>
                            {t('calculation-details.payment-date') + ' '}
                            {partialSettlementPaymentDate
                                ? f(partialSettlementPaymentDate, TranslationFormat.DATE)
                                : f(paymentDate, TranslationFormat.DATE)}
                        </div>
                        <div>
                            {t('calculation-details.next-due-date') + ' '}
                            {f(nextDueDate, TranslationFormat.DATE)}
                        </div>
                    </>
                }
                totalValue={<div className="u-font-size-fs4">{f(totalAmount, TranslationFormat.CURRENCY)}</div>}
            >
                <CalculationDetailsRow
                    title={t('penalty-amount-details.header.headline').toUpperCase()}
                    value={f(subsidyAmount, TranslationFormat.CURRENCY)}
                    details={subsidyFields}
                    tooltip={t('penalty-amount-details.header.tooltip').toUpperCase()}
                />
                <CalculationDetailsRow
                    title={t('calculation-details.header.headline').toUpperCase()}
                    value={f(settlementAmount, TranslationFormat.CURRENCY)}
                    details={settlementFields}
                />
                <DescriptionListAccordion.Item
                    nonCollapsable
                    title={unpaidBalanceTitle}
                    titleSplit={f(unpaidBalanceAmount, TranslationFormat.CURRENCY)}
                    icon="semantic-info"
                    tooltip={t('calculation-details.unpaid-balance.tooltip')}
                />
            </DescriptionListAccordion>
        </div>
    );
};
